import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Observable, catchError, concatMap, defaultIfEmpty, from, map, of, take } from 'rxjs';
import { AiAssistantService, AiChatRefusedError } from './ai-assistant.service';
import { AiAttachmentRef, AiRowOutcome, AiThreadMessage, fromResponse, fromServerMessage, noticeMessage, userMessage } from './ai-chat.models';

/**
 * "The current chat" for Crystal web — shared by the header bar, side panel, Home block and the
 * full page, so a chat started anywhere continues everywhere. Server-owned sessions: this holds a
 * view of one session, never the source of truth.
 */
@Injectable({ providedIn: 'root' })
export class AiChatStore {
  readonly sessionId = signal<string | null>(null);
  readonly title = signal('New chat');
  readonly caseReference = signal<string | null>(null);
  readonly messages = signal<AiThreadMessage[]>([]);
  readonly sending = signal(false);
  readonly loading = signal(false);
  readonly notFound = signal(false);
  /** Bumped whenever the chat list may have changed (new chat, title, delete) — lists reload on it. */
  readonly listVersion = signal(0);

  /** Bumped by newChat()/open(); a reply or load that started under an older epoch is dropped. */
  private epoch = 0;

  constructor(private api: AiAssistantService) {}

  newChat(): void {
    this.epoch++;
    this.sending.set(false);
    this.loading.set(false);
    this.sessionId.set(null);
    this.title.set('New chat');
    this.caseReference.set(null);
    this.messages.set([]);
    this.notFound.set(false);
  }

  open(id: string): void {
    if (this.sessionId() === id && !this.notFound()) return;
    const epoch = ++this.epoch;
    this.sending.set(false);
    this.loading.set(true);
    this.notFound.set(false);
    this.api.getSession(id).subscribe({
      next: (r) => {
        if (epoch !== this.epoch) return;
        this.loading.set(false);
        this.sessionId.set(r.Session?.SessionId ?? id);
        this.title.set(r.Session?.Title || 'Chat');
        this.caseReference.set(r.Session?.CaseReference ?? null);
        this.messages.set((r.Messages ?? []).map(fromServerMessage));
      },
      error: (err) => {
        if (epoch !== this.epoch) return;
        this.loading.set(false);
        const status = err instanceof HttpErrorResponse ? err.status : 0;
        if (status === 404) {
          this.newChat();
          this.notFound.set(true);
        } else {
          // Transient failure: keep the current chat and tell the user.
          this.messages.update((m) => [...m, noticeMessage('Could not open that chat. Please try again.')]);
        }
      },
    });
  }

  /** pageCaseId is only used when this starts a new chat. */
  send(text: string, attachments: AiAttachmentRef[], pageCaseId: number | null = null): void {
    const message = text.trim();
    if ((!message && attachments.length === 0) || this.sending()) return;
    const sent = userMessage(message, attachments);
    this.messages.update((m) => [...m, sent]);
    this.dispatch(sent, pageCaseId);
  }

  /** Resend a failed/refused user message: same text, same attachments (the server unbinds them on failure). */
  retry(failed: AiThreadMessage): void {
    if (this.sending() || failed.role !== 'user' || !failed.failed) return;
    this.messages.update((m) => m.map((x) => (x.localId === failed.localId ? { ...x, failed: false } : x)));
    this.dispatch({ ...failed, failed: false }, null);
  }

  /** A confirmation card was cancelled: freeze it and add the outcome line. */
  resolveDraft(localId: number, notice: string): void {
    this.messages.update((m) => [
      ...m.map((x) => (x.localId === localId ? { ...x, draftActionable: false } : x)),
      noticeMessage(notice),
    ]);
  }

  /**
   * Confirm a draft exactly once. The message is made non-actionable BEFORE the save starts and the
   * outcome is recorded here, so it survives the card/panel being destroyed mid-save. On failure the
   * draft becomes actionable again (nothing was saved). done(ok) lets a live card show a toast.
   */
  confirmDraft(localId: number, save: () => Observable<unknown>, successNotice: string, done?: (ok: boolean) => void): void {
    const msg = this.messages().find((x) => x.localId === localId);
    if (!msg || !msg.draftActionable) return;
    this.setActionable(localId, false);
    let saved = false;
    const failed = () => {
      this.setActionable(localId, true);
      done?.(false);
    };
    save().subscribe({
      next: () => {
        saved = true;
        this.messages.update((m) => [...m, noticeMessage(successNotice)]);
        done?.(true);
      },
      error: failed,
      // Ending without a response means nothing was confirmed saved: never leave the card greyed out.
      complete: () => { if (!saved) failed(); },
    });
  }

  /** P171: batch rows that were saved or removed. When none is left open the card is frozen (with the notice, if any). */
  markRows(localId: number, outcomes: Record<number, AiRowOutcome>, rowCount: number, notice?: string): void {
    this.messages.update((m) => m.map((x) => (x.localId === localId ? { ...x, rowOutcomes: { ...x.rowOutcomes, ...outcomes } } : x)));
    const msg = this.messages().find((x) => x.localId === localId);
    if (!msg || Object.keys(msg.rowOutcomes ?? {}).length < rowCount) return;
    this.setActionable(localId, false);
    if (notice) this.messages.update((m) => [...m, noticeMessage(notice)]);
  }

  /**
   * P171 Confirm all: every row through its own save (the single-entry path), one after another; a failing row never
   * stops the others. The card is frozen while saving and saved rows are recorded here, so a card destroyed mid-save
   * never offers them again. Rows that failed keep the card actionable for a retry. rowDone feeds a live card.
   */
  confirmDraftRows(localId: number, rowCount: number, saves: { row: number; save: () => Observable<unknown> }[],
    rowDone: (row: number, ok: boolean, error?: string) => void, done?: (saved: number, failed: number) => void): void {
    const msg = this.messages().find((x) => x.localId === localId);
    if (!msg || !msg.draftActionable || saves.length === 0) return;
    this.setActionable(localId, false);
    let saved = 0;
    let failed = 0;
    from(saves).pipe(
      concatMap(({ row, save }) => save().pipe(
        take(1),
        map(() => ({ row, ok: true, error: undefined as string | undefined })),
        // Ending without a response means nothing was confirmed saved.
        defaultIfEmpty({ row, ok: false, error: 'No response from Crystal.' }),
        catchError((err: unknown) => of({ row, ok: false, error: saveError(err) })),
      )),
    ).subscribe({
      next: ({ row, ok, error }) => {
        if (ok) {
          saved++;
          this.messages.update((m) => m.map((x) => (x.localId === localId ? { ...x, rowOutcomes: { ...x.rowOutcomes, [row]: 'saved' } } : x)));
        } else {
          failed++;
        }
        rowDone(row, ok, error);
      },
      complete: () => {
        const notice = failed === 0
          ? `${saved} time ${saved === 1 ? 'entry' : 'entries'} created.`
          : `${saved} of ${saved + failed} time entries created; ${failed} failed (see the card).`;
        this.messages.update((m) => [...m, noticeMessage(notice)]);
        const left = rowCount - Object.keys(this.messages().find((x) => x.localId === localId)?.rowOutcomes ?? {}).length;
        if (left > 0) this.setActionable(localId, true);
        done?.(saved, failed);
      },
    });
  }

  refreshHeader(title: string, caseReference: string | null): void {
    this.title.set(title);
    this.caseReference.set(caseReference);
    this.listVersion.update((v) => v + 1);
  }

  private setActionable(localId: number, v: boolean): void {
    this.messages.update((m) => m.map((x) => (x.localId === localId ? { ...x, draftActionable: v } : x)));
  }

  private dispatch(sent: AiThreadMessage, pageCaseId: number | null): void {
    const isNew = this.sessionId() == null;
    const epoch = this.epoch;
    this.sending.set(true);
    this.api
      .send({
        sessionId: this.sessionId(),
        message: sent.text,
        attachmentIds: sent.attachments.map((a) => a.id),
        surface: 'Web',
        pageCaseId: isNew ? pageCaseId : null,
      })
      .subscribe({
        next: (r) => {
          if (epoch !== this.epoch) return;
          this.sending.set(false);
          if (r.SessionId) this.sessionId.set(r.SessionId);
          if (r.Title) this.title.set(r.Title);
          this.caseReference.set(r.CaseReference ?? null);
          if (r.Type === 'error') {
            this.markFailed(sent.localId, r.Text || 'Crystal AI could not answer just now. Please try again.');
          } else {
            const reply = fromResponse(r);
            // A new draft is a correction of any earlier one: only the latest may be confirmed.
            this.messages.update((m) => [
              ...(reply.draftActionable ? m.map((x) => (x.draftActionable ? { ...x, draftActionable: false } : x)) : m),
              reply,
            ]);
          }
          this.listVersion.update((v) => v + 1);
        },
        error: (err) => {
          if (epoch !== this.epoch) return;
          this.sending.set(false);
          const text = err instanceof AiChatRefusedError
            ? err.message
            : 'Crystal AI could not answer just now. Please try again.';
          this.markFailed(sent.localId, text);
        },
      });
  }

  private markFailed(localId: number, notice: string): void {
    this.messages.update((m) => [
      ...m.map((x) => (x.localId === localId ? { ...x, failed: true } : x)),
      noticeMessage(notice),
    ]);
  }
}

/** The API's own message for a failed save (FastEndpoints error body), or a generic one. */
function saveError(err: unknown): string {
  const body = err instanceof HttpErrorResponse ? err.error : null;
  const errors = body?.Errors ?? body?.errors;
  const first = errors && typeof errors === 'object'
    ? Object.values(errors as Record<string, unknown>).flat().find((v) => typeof v === 'string')
    : null;
  return (first as string | null) ?? body?.Message ?? body?.message ?? 'The save failed.';
}
